import React, { useMemo, useState } from "react";
import { Reveal } from "../components/Reveal";
import { Gauge, Ruler } from "lucide-react";

// ---------- Tire parsing ----------
function parseTire(str) {
  if (!str) return null;
  const s = str.trim().toUpperCase();
  // Flotation: 33X12.50R20
  let m = s.match(/^(\d+(?:\.\d+)?)X(\d+(?:\.\d+)?)R?(\d+(?:\.\d+)?)$/);
  if (m) {
    const dia = parseFloat(m[1]);
    const width = parseFloat(m[2]);
    const rim = parseFloat(m[3]);
    return { diameter: dia, width, rim, sidewall: (dia - rim) / 2 };
  }
  // Metric: 285/70R17
  m = s.match(/^(\d+)\/(\d+)R?(\d+(?:\.\d+)?)$/);
  if (m) {
    const section = parseInt(m[1], 10);
    const aspect = parseInt(m[2], 10);
    const rim = parseFloat(m[3]);
    const sidewallMM = section * (aspect / 100);
    const dia = rim + (2 * sidewallMM) / 25.4;
    return { diameter: dia, width: section / 25.4, rim, sidewall: sidewallMM / 25.4 };
  }
  return null;
}

function TireCompare() {
  const [a, setA] = useState("285/70R17");
  const [b, setB] = useState("33x12.50R20");
  const ta = useMemo(() => parseTire(a), [a]);
  const tb = useMemo(() => parseTire(b), [b]);

  const stat = (t) => {
    if (!t) return null;
    const circ = Math.PI * t.diameter;
    return {
      diameter: t.diameter.toFixed(2),
      width: t.width.toFixed(2),
      sidewall: t.sidewall.toFixed(2),
      circ: circ.toFixed(2),
      revs: (63360 / circ).toFixed(0),
    };
  };
  const sa = stat(ta), sb = stat(tb);

  const diff = ta && tb ? ((tb.diameter - ta.diameter) / ta.diameter) * 100 : 0;
  const speedoAt60 = ta && tb ? (60 * (tb.diameter / ta.diameter)).toFixed(1) : null;
  const maxDia = Math.max(ta?.diameter || 1, tb?.diameter || 1);

  const rows = [
    ["Overall Diameter", "diameter", "in"],
    ["Section Width", "width", "in"],
    ["Sidewall Height", "sidewall", "in"],
    ["Circumference", "circ", "in"],
    ["Revs / Mile", "revs", ""],
  ];

  return (
    <div className="border border-[#27272a] bg-[#0a0a0a]">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-[#27272a]">
        <Ruler className="text-[#00f0ff]" size={20} />
        <h3 className="font-display text-2xl uppercase">Tire Size Comparison</h3>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-2 gap-6 mb-8">
          {[["Tire A", a, setA], ["Tire B", b, setB]].map(([label, val, setter], i) => (
            <div key={label}>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">{label}</label>
              <input
                value={val}
                onChange={(e) => setter(e.target.value)}
                data-testid={`tire-input-${i}`}
                placeholder="285/70R17 or 33x12.50R20"
                className="w-full bg-[#111] border-b-2 border-[#27272a] focus:border-[#00f0ff] py-2.5 px-3 outline-none font-mono text-sm transition-colors"
              />
            </div>
          ))}
        </div>

        {/* Visual */}
        <div className="flex items-end justify-center gap-10 h-52 mb-8">
          {[ta, tb].map((t, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div
                className={`rounded-full border-4 ${i === 0 ? "border-[#00f0ff]" : "border-[#ff7300]"} bg-[#111] flex items-center justify-center`}
                style={{ height: `${((t?.diameter || 1) / maxDia) * 180}px`, width: `${((t?.diameter || 1) / maxDia) * 180}px` }}
              >
                <span className="text-xs text-zinc-400 font-mono">{t ? `${t.diameter.toFixed(1)}"` : "—"}</span>
              </div>
              <span className="text-xs uppercase tracking-widest text-zinc-500">Tire {i === 0 ? "A" : "B"}</span>
            </div>
          ))}
        </div>

        <table className="w-full text-sm" data-testid="tire-compare-table">
          <thead>
            <tr className="text-zinc-500 text-xs uppercase tracking-widest">
              <th className="text-left py-2">Spec</th>
              <th className="text-right py-2 text-[#00f0ff]">A</th>
              <th className="text-right py-2 text-[#ff7300]">B</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, key, unit], i) => (
              <tr key={key} className={i % 2 ? "bg-[#0c0c0c]" : ""}>
                <td className="py-2.5 px-2 text-zinc-400">{label}</td>
                <td className="py-2.5 px-2 text-right font-mono">{sa ? `${sa[key]}${unit ? " " + unit : ""}` : "—"}</td>
                <td className="py-2.5 px-2 text-right font-mono">{sb ? `${sb[key]}${unit ? " " + unit : ""}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {ta && tb && (
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="border border-[#27272a] p-4">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Diameter Change</div>
              <div className={`font-display text-3xl ${diff >= 0 ? "text-emerald-400" : "text-amber-400"}`} data-testid="diameter-diff">
                {diff >= 0 ? "+" : ""}{diff.toFixed(1)}%
              </div>
            </div>
            <div className="border border-[#27272a] p-4">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Speedo reads 60 → actual</div>
              <div className="font-display text-3xl text-white" data-testid="speedo-diff">{speedoAt60} mph</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Offset calculator ----------
function OffsetCalc() {
  const [width, setWidth] = useState(9);
  const [oldOff, setOldOff] = useState(0);
  const [newOff, setNewOff] = useState(-24);

  const backspace = (off) => (width / 2 + off / 25.4 + 0.5).toFixed(2);
  const poke = ((oldOff - newOff) / 25.4).toFixed(2);

  return (
    <div className="border border-[#27272a] bg-[#0a0a0a]">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-[#27272a]">
        <Gauge className="text-[#00f0ff]" size={20} />
        <h3 className="font-display text-2xl uppercase">Offset & Backspacing</h3>
      </div>
      <div className="p-6 space-y-5">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">Wheel Width: {width}"</label>
          <input type="range" min="7" max="14" step="0.5" value={width} onChange={(e) => setWidth(Number(e.target.value))} data-testid="offset-width" className="w-full accent-[#00f0ff]" />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">Current Offset: {oldOff}mm</label>
            <input type="range" min="-50" max="50" value={oldOff} onChange={(e) => setOldOff(Number(e.target.value))} data-testid="offset-old" className="w-full accent-[#00f0ff]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#ff7300] mb-1">New Offset: {newOff}mm</label>
            <input type="range" min="-50" max="50" value={newOff} onChange={(e) => setNewOff(Number(e.target.value))} data-testid="offset-new" className="w-full accent-[#ff7300]" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-4">
          <div className="border border-[#27272a] p-4">
            <div className="text-xs uppercase tracking-widest text-zinc-500">New Backspacing</div>
            <div className="font-display text-3xl text-white" data-testid="backspacing-result">{backspace(newOff)}"</div>
            <div className="text-xs text-zinc-500 mt-1">was {backspace(oldOff)}"</div>
          </div>
          <div className="border border-[#27272a] p-4">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Additional Poke</div>
            <div className={`font-display text-3xl ${poke >= 0 ? "text-emerald-400" : "text-amber-400"}`} data-testid="poke-result">
              {poke >= 0 ? "+" : ""}{poke}"
            </div>
            <div className="text-xs text-zinc-500 mt-1">how much further out</div>
          </div>
        </div>

        {/* stance visual */}
        <div className="relative h-24 border border-[#27272a] bg-[#0c0c0c] overflow-hidden mt-2">
          <div className="absolute top-0 bottom-0 left-1/2 w-px bg-zinc-700" />
          <div className="absolute top-1/2 -translate-y-1/2 h-10 bg-[#00f0ff]/60" style={{ left: "50%", width: `${width * 6}px`, transform: `translate(${-oldOff / 2}px, -50%)` }} />
          <div className="absolute top-1/2 -translate-y-1/2 h-10 bg-[#ff7300]/60" style={{ left: "50%", width: `${width * 6}px`, transform: `translate(${-newOff / 2}px, -50%)` }} />
          <span className="absolute bottom-1 left-2 text-[10px] uppercase tracking-widest text-zinc-500">Fender line ← → hub</span>
        </div>
        <p className="text-xs text-zinc-500">Lower / negative offset pushes the wheel outward for a wider, more aggressive stance. Verify clearance before ordering.</p>
      </div>
    </div>
  );
}

export default function Tools() {
  return (
    <div className="pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.3em] text-[#00f0ff]">Build Lab</span>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-none mt-3">Fitment <span className="bttf-text">Tools</span></h1>
          <p className="text-zinc-400 max-w-lg mt-4">Run the numbers before you buy. Dial in offset, backspacing and tire diameter changes with live calculators.</p>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-8 mt-14">
          <Reveal><OffsetCalc /></Reveal>
          <Reveal delay={0.1}><TireCompare /></Reveal>
        </div>
      </div>
    </div>
  );
}
